import { type ConsentGate, type ConsentKey } from './consent.ts'

/**
 * Browser side of the Meta and TikTok pixels for itsbaba.com and
 * app.itsbaba.com (ad-measurement contract 2026-10-20). Both pixels load only
 * after the consent gate grants ad_storage; events with a server twin carry
 * the server's event id so Meta and TikTok drop the duplicate.
 */

type AnyFn = (...args: unknown[]) => unknown
interface PixelWindow {
  fbq?: AnyFn
  _fbq?: unknown
  ttq?: Record<string, unknown> & { methods?: string[]; push?: AnyFn; load?: AnyFn; instance?: AnyFn; _i?: Record<string, unknown> }
  TiktokAnalyticsObject?: string
  babaConsent?: ConsentGate
}
const win = (): PixelWindow | null => (typeof window === 'undefined' ? null : (window as unknown as PixelWindow))

export function consentGate(): ConsentGate | null {
  return win()?.babaConsent ?? null
}

/** Run `fn` once `key` is granted. With no gate on the page it never runs: fail closed. */
export function whenConsented(key: ConsentKey, fn: () => void): void {
  consentGate()?.when(key, fn)
}

export function newEventId(): string {
  try { return crypto.randomUUID() } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`
  }
}

let metaPixelId = ''
let tiktokPixelId = ''

/** Idempotent. Call from inside whenConsented('ad_storage', …). */
export function loadMetaPixel(pixelId: string): void {
  const w = win()
  if (!w || !pixelId || w.fbq) return
  metaPixelId = pixelId
  const stub = function (...args: unknown[]) {
    const self = stub as unknown as { callMethod?: AnyFn; queue: unknown[][] }
    if (self.callMethod) self.callMethod(...args)
    else self.queue.push(args)
  } as unknown as AnyFn & { push: unknown; loaded: boolean; version: string; queue: unknown[][] }
  if (!w._fbq) w._fbq = stub
  stub.push = stub
  stub.loaded = true
  stub.version = '2.0'
  stub.queue = []
  w.fbq = stub
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)
  const state = consentGate()?.get()
  w.fbq('consent', 'grant')
  if (state?.do_not_sell) w.fbq('dataProcessingOptions', ['LDU'], 0, 0)
  w.fbq('init', pixelId)
  w.fbq('track', 'PageView')
}

const TIKTOK_METHODS = [
  'page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group',
  'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent',
]

/** Idempotent. Call from inside whenConsented('ad_storage', …). */
export function loadTikTokPixel(pixelId: string): void {
  const w = win()
  if (!w || !pixelId || w.ttq) return
  tiktokPixelId = pixelId
  w.TiktokAnalyticsObject = 'ttq'
  const ttq = (w.ttq = [] as unknown as NonNullable<PixelWindow['ttq']>)
  ttq.methods = TIKTOK_METHODS
  const defer = (target: Record<string, unknown>, method: string) => {
    target[method] = (...args: unknown[]) => { (target as unknown as { push: AnyFn }).push([method, ...args]) }
  }
  for (const method of TIKTOK_METHODS) defer(ttq, method)
  ttq.instance = (id: unknown) => {
    const inst = ((ttq._i ?? {})[id as string] ?? []) as unknown as Record<string, unknown>
    for (const method of TIKTOK_METHODS) defer(inst, method)
    return inst
  }
  ttq.load = (id: unknown, options: unknown) => {
    const src = 'https://analytics.tiktok.com/i18n/pixel/events.js'
    const key = id as string
    const bag = (ttq as unknown as Record<string, Record<string, unknown>>)
    ttq._i = ttq._i || {}
    ;(ttq._i as Record<string, unknown>)[key] = Object.assign([], { _u: src })
    bag._t = bag._t || {}
    bag._t[key] = Date.now()
    bag._o = bag._o || {}
    bag._o[key] = options || {}
    const script = document.createElement('script')
    script.async = true
    script.src = `${src}?sdkid=${encodeURIComponent(key)}&lib=ttq`
    document.head.appendChild(script)
  }
  const gate = consentGate()
  ;(ttq.holdConsent as AnyFn)()
  ttq.load(pixelId, gate?.get()?.do_not_sell ? { limited_data_use: true } : {})
  ;(ttq.page as AnyFn)()
  if (gate?.allowed('ad_storage')) (ttq.grantConsent as AnyFn)()
}

export type AdEventName = 'ViewContent' | 'Lead' | 'Contact' | 'CompleteRegistration' | 'InitiateCheckout'

/** TikTok has no Lead; the contract sends a lead or contact as its Contact. */
const TIKTOK_EVENT: Record<AdEventName, string> = {
  ViewContent: 'ViewContent',
  Lead: 'Contact',
  Contact: 'Contact',
  CompleteRegistration: 'CompleteRegistration',
  InitiateCheckout: 'InitiateCheckout',
}

/**
 * Send one standard event to whichever pixels are on the page. `eventId` is the
 * server's id for events it also sends (`reg_<userId>`, the Stripe session id)
 * and a fresh UUID for browser-only events.
 */
export function trackAdEvent(name: AdEventName, params: Record<string, unknown> | undefined, eventId: string = newEventId()): void {
  const w = win()
  if (!w) return
  try { w.fbq?.('track', name, params ?? {}, { eventID: eventId }) } catch { /* A blocked pixel must not break the page. */ }
  try {
    const ttq = w.ttq
    if (ttq && typeof ttq.track === 'function') (ttq.track as AnyFn)(TIKTOK_EVENT[name], params ?? {}, { event_id: eventId })
  } catch { /* Same. */ }
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Advanced matching after sign-in. Meta hashes `em` and `external_id` itself, so
 * it gets the normalized plain values; TikTok's identify wants SHA-256 hex.
 * Needs ad_user_data as well as ad_storage, and does nothing otherwise.
 */
export function identifyAdUser(user: { email?: string | null; externalId?: string | null }): void {
  const w = win()
  const gate = consentGate()
  if (!w || !gate || !gate.allowed('ad_storage') || !gate.allowed('ad_user_data')) return
  const email = user.email ? normalizeEmail(user.email) : ''
  const externalId = user.externalId?.trim() ?? ''
  if (!email && !externalId) return
  try {
    if (w.fbq && metaPixelId) {
      w.fbq('init', metaPixelId, { ...(email ? { em: email } : {}), ...(externalId ? { external_id: externalId } : {}) })
    }
  } catch { /* Same. */ }
  if (w.ttq && tiktokPixelId && typeof w.ttq.identify === 'function') {
    const identify = w.ttq.identify as AnyFn
    void Promise.all([email ? sha256Hex(email) : '', externalId ? sha256Hex(externalId) : '']).then(([em, id]) => {
      identify({ ...(em ? { email: em } : {}), ...(id ? { external_id: id } : {}) })
    }).catch(() => {})
  }
}
